import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Chip, Button, Stack, Loading } from "@curves/ui-base";
import { Html5QrcodeSupportedFormats, Html5Qrcode } from "html5-qrcode";
import { v4 as uuid } from "uuid";
import { isEmpty, getIsMobile, isFunction } from "@util/utils";

/**
 * 通用條碼掃描器
 * 此元件支援多種條碼格式，可彈性用於掃描載具、會員碼或一般資料。
 * @param {{
 *   onScanSuccess?: (decodedText: string) => void,
 *   onScanError?: (errorMessage: string) => void,
 *   hasButton: boolean,
 *   title?: string,
 *   supportedFormats?: Html5QrcodeSupportedFormats[],
 *   continuousScan?: boolean
 * }} props - 掃描成功與錯誤的回呼函式
 */
const BarcodeScanner = ({
	onScanSuccess = () => {},
	onScanError = () => {},
	hasButton = false,
	title = "掃描結果",
	supportedFormats = [Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.CODE_39, Html5QrcodeSupportedFormats.QR_CODE, Html5QrcodeSupportedFormats.DATA_MATRIX],
	continuousScan = false,
}) => {
	const [targetDom] = useState(`qr-reader-${uuid()}`);
	const [barCodeValue, setBarCodeValue] = useState(null);
	const [isScanning, setIsScanning] = useState(false);
	const [cameras, setCameras] = useState([]);
	const [currentCameraId, setCurrentCameraId] = useState(null);
	const [isRequestingPermission, setIsRequestingPermission] = useState(false);
	const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);

	const html5QrCode = useRef(null);
	const lastScannedValue = useRef(null);
	const isMountedRef = useRef(true);
	const scanTimeoutRef = useRef(null);

	// 安全的狀態更新
	const safeSetState = useCallback((setter, value) => {
		if (isMountedRef.current) {
			setter(value);
		}
	}, []);

	// 掃描成功回調
	const onScanResult = useCallback(
		(decodedText, _decodedResult) => {
			// 連續掃描模式：允許處理不同的條碼
			if (continuousScan) {
				if (decodedText !== lastScannedValue.current) {
					lastScannedValue.current = decodedText;
					safeSetState(setBarCodeValue, decodedText);

					isFunction(onScanSuccess) && onScanSuccess(decodedText);

					// 設置一個延遲，允許重新掃描相同的條碼
					setTimeout(() => {
						if (lastScannedValue.current === decodedText) {
							lastScannedValue.current = null;
						}
					}, 1000); // 1秒後允許重新掃描
				}
			} else {
				// 非連續掃描模式
				if (decodedText !== lastScannedValue.current) {
					lastScannedValue.current = decodedText;
					safeSetState(setBarCodeValue, decodedText);
					isFunction(onScanSuccess) && onScanSuccess(decodedText);
					stopScanning();
				}
			}
		},
		// eslint-disable-next-line
		[onScanSuccess, safeSetState, continuousScan],
	);

	// 掃描錯誤回調
	const qrCodeErrorCallback = useCallback(
		errorMessage => {
			if (!errorMessage.includes("NotFoundException") && !errorMessage.includes("No MultiFormat Readers")) {
				onScanError(errorMessage);
				console.log("⚠️ 掃描錯誤:", errorMessage);
			}
		},
		[onScanError],
	);

	// 停止掃描
	const stopScanning = useCallback(async () => {
		try {
			if (html5QrCode.current) {
				// 檢查掃描器是否真的在運行
				if (html5QrCode.current.isScanning) {
					await html5QrCode.current.stop();
				}
				html5QrCode.current.clear();
			}
			safeSetState(setIsScanning, false);
		} catch (error) {
			console.error("停止掃描時發生錯誤:", error);
		}
	}, [safeSetState]);

	// 初始化掃描器
	const initializeScanner = useCallback(
		async cameraId => {
			try {
				// 確保 DOM 元素存在
				const targetElement = document.getElementById(targetDom);
				if (!targetElement) {
					console.error("目標 DOM 元素不存在:", targetDom);
					return;
				}

				// 清理舊的實例
				if (html5QrCode.current) {
					try {
						if (html5QrCode.current.isScanning) {
							await html5QrCode.current.stop();
						}
						html5QrCode.current.clear();
					} catch (e) {
						console.log("清理舊實例時發生錯誤:", e);
					}
				}

				// 創建新實例
				html5QrCode.current = new Html5Qrcode(targetDom);

				const config = {
					fps: getIsMobile() ? 15 : 20,
					qrbox: getIsMobile() ? { width: 320, height: 140 } : { width: 400, height: 180 },
					formatsToSupport: supportedFormats,
					disableFlip: false,
					experimentalFeatures: {
						useBarCodeDetectorIfSupported: true,
					},
				};

				await html5QrCode.current.start(cameraId, config, onScanResult, qrCodeErrorCallback);
				safeSetState(setCurrentCameraId, cameraId);
				safeSetState(setIsScanning, true);
			} catch (error) {
				console.error("啟動相機失敗:", error);
				safeSetState(setIsScanning, false);
			}
		},
		[targetDom, supportedFormats, onScanResult, qrCodeErrorCallback, safeSetState],
	);

	// 啟動掃描
	const startScanning = useCallback(
		async (cameraId = null) => {
			safeSetState(setIsRequestingPermission, true);

			try {
				const availableCameras = await Html5Qrcode.getCameras();
				if (availableCameras && availableCameras.length) {
					const filteredCameras = availableCameras.filter(cam => !/ultrawide|超廣角/i.test(cam.label));
					safeSetState(setCameras, filteredCameras);

					const preferredCamera =
						filteredCameras.find(cam => /back|rear|environment|wide|後置|後鏡/i.test(cam.label) && !/ultrawide|超廣角/i.test(cam.label)) || filteredCameras[filteredCameras.length - 1];

					const selectedId = cameraId || preferredCamera?.id;
					await initializeScanner(selectedId);
				} else {
					console.error("沒有找到可用的相機");
				}
			} catch (error) {
				console.error("無法啟動相機:", error);
			} finally {
				safeSetState(setIsRequestingPermission, false);
			}
		},
		[initializeScanner, safeSetState],
	);

	// 重置掃描結果
	const resetScanResult = useCallback(() => {
		safeSetState(setBarCodeValue, null);
		lastScannedValue.current = null;
		// 不調用 onScanSuccess(null)，避免外部狀態混亂
	}, [safeSetState]);

	// 切換鏡頭
	const switchCamera = useCallback(async () => {
		if (cameras.length < 2 || isSwitchingCamera) return;

		safeSetState(setIsSwitchingCamera, true);

		try {
			const currentIndex = cameras.findIndex(cam => cam.id === currentCameraId);
			const nextIndex = (currentIndex + 1) % cameras.length;
			const nextCameraId = cameras[nextIndex].id;

			await stopScanning();
			await new Promise(resolve => setTimeout(resolve, 300));
			await initializeScanner(nextCameraId);
		} catch (error) {
			console.error("切換鏡頭失敗:", error);
		} finally {
			safeSetState(setIsSwitchingCamera, false);
		}
	}, [cameras, currentCameraId, isSwitchingCamera, stopScanning, initializeScanner, safeSetState]);

	// 組件掛載時自動開始掃描
	useEffect(() => {
		if (!hasButton) {
			startScanning();
		}
	}, [hasButton, startScanning]);

	// 組件卸載時清理資源
	useEffect(() => {
		return () => {
			isMountedRef.current = false;

			// 將 ref 值複製到變數中，避免在清理函數中使用可能改變的 ref
			// eslint-disable-next-line react-hooks/exhaustive-deps
			const currentTimeout = scanTimeoutRef.current;
			if (currentTimeout) {
				clearTimeout(currentTimeout);
			}

			if (html5QrCode.current) {
				try {
					if (html5QrCode.current.isScanning) {
						console.count("掃描器卸載停止中");
						html5QrCode.current.stop().catch(err => console.log("掃描停止錯誤:", err));
					}
					console.count("掃描器卸載清理中");
					html5QrCode.current.clear();
				} catch (error) {
					console.log("清理掃描器時發生錯誤:", error);
				}
			}
		};
	}, []); // 空依賴陣列，只在組件卸載時執行

	return (
		<Box style={{ width: 500, maxWidth: "100%", margin: "auto", position: "relative", borderRadius: 20 }}>
			{isScanning && (
				<>
					<Chip className="mb-2" size="large" label={`${title}：${barCodeValue || "等待掃描..."}`} color={isEmpty(barCodeValue) ? "error" : "success"} />

					<Stack
						style={{
							position: "absolute",
							zIndex: 10,
							top: 0,
							right: 0,
						}}
					>
						{barCodeValue && (
							<Button onClick={resetScanResult} variant="text" size="small" style={{ marginLeft: 8 }}>
								清除
							</Button>
						)}
						{isScanning && cameras.length > 1 && (
							<Button onClick={switchCamera} variant="outlined" size="small" disabled={isSwitchingCamera}>
								{isSwitchingCamera ? "切換中..." : "切換鏡頭"}
							</Button>
						)}
					</Stack>
				</>
			)}

			<Box>
				{!isScanning && (
					<Box
						style={{
							backgroundColor: "rgba(0, 0, 0, 0.48)",
							height: 300,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							borderRadius: 10,
							boxShadow: "2px 4px 16px rgba(0, 0, 0, 0.3)",
						}}
					>
						<Button variant="contained" size={"xsmall"} color={"blue-gray"} onClick={() => startScanning()} disabled={isRequestingPermission}>
							開啟相機掃描
						</Button>
					</Box>
				)}
				<Box
					style={{
						borderRadius: 10,
						overflow: "hidden",
						boxShadow: "2px 4px 16px rgba(0, 0, 0, 0.3)",
						position: "relative",
					}}
				>
					{(isRequestingPermission || isSwitchingCamera) && (
						<Box
							style={{
								backgroundColor: "rgba(0, 0, 0, 0.48)",
								height: 300,
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								zIndex: 5,
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<Loading style={{ width: 50 }} size={50} duration={500} />
							<Box style={{ color: "#fff", marginTop: 16 }}>
								{isRequestingPermission && "請求相機權限..."}
								{isSwitchingCamera && "切換鏡頭中..."}
							</Box>
						</Box>
					)}
					<Box id={targetDom} style={{ position: "relative", overflow: "hidden" }}></Box>
				</Box>
			</Box>
		</Box>
	);
};

export default BarcodeScanner;
